import { Fragment, ReactNode } from "react";

import { cn } from "@/app/utils/cn";

const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<]+)/gi;
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/;

export interface TextPart {
  type: "text" | "link";
  value: string;
  href?: string;
}

// Quebra o texto em trechos de texto puro e links (http://, https:// e www.).
// A pontuação no fim da URL ("veja https://x.com.") fica fora do link.
export const splitTextWithLinks = (text: string): TextPart[] => {
  const parts: TextPart[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(URL_REGEX)) {
    const start = match.index ?? 0;
    const raw = match[0];
    const trailing = raw.match(TRAILING_PUNCTUATION)?.[0] ?? "";
    const url = trailing ? raw.slice(0, -trailing.length) : raw;

    if (!url) continue;

    if (start > lastIndex) {
      parts.push({ type: "text", value: text.slice(lastIndex, start) });
    }

    parts.push({
      type: "link",
      value: url,
      href: /^www\./i.test(url) ? `https://${url}` : url,
    });

    lastIndex = start + url.length;
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", value: text.slice(lastIndex) });
  }

  return parts;
};

interface LinkifiedTextProps {
  text: string;
  className?: string;
  linkClassName?: string;
}

export const LinkifiedText = ({
  text,
  className,
  linkClassName,
}: LinkifiedTextProps): ReactNode => (
  <span className={cn("select-text whitespace-break-spaces", className)}>
    {splitTextWithLinks(text).map((part, index) => (
      <Fragment key={index}>
        {part.type === "link" ? (
          <a
            href={part.href}
            target="_blank"
            rel="noopener noreferrer"
            className={cn("break-all underline", linkClassName)}
          >
            {part.value}
          </a>
        ) : (
          part.value
        )}
      </Fragment>
    ))}
  </span>
);
